/*
Badge catalogue — mirrors Android Badge.kt.
15 badges across 6 categories. Definitions are static; the unlock state
is computed at view time by `badgeEvaluator.ts` from captures + arbres
features + species-info, no IDB table.

Adding a badge:
  1. Append a BadgeDef to kBadgeCatalog.
  2. Add a criterion in evaluateBadges (`badgeEvaluator.ts`).
  3. Optionally add an emoji glyph in kBadgeGlyphs below.
*/

export type BadgeCategory =
	| 'decouverte'
	| 'botanique'
	| 'geographie'
	| 'remarquables'
	| 'saisons'
	| 'demesure'

export const kBadgeCategoryLabels: Record<BadgeCategory, string> = {
	decouverte: 'Découverte',
	botanique: 'Botanique',
	geographie: 'Géographie',
	remarquables: 'Remarquables',
	saisons: 'Saisons',
	demesure: 'Démesure',
}

export interface BadgeDef
{
	id: string
	label: string
	description: string
	category: BadgeCategory
	implemented: boolean  // true when the PWA dataset carries enough info to evaluate this badge
}

export interface BadgeState
{
	def: BadgeDef
	unlockedAt: number | null  // epoch ms of the capture that unlocked the badge, null when still locked
}

/*
Static catalogue. Order is meaningful — the badges screen displays them
grouped by `category` in this order. All 15 badges are now evaluable:
the per-arbre details file (arbres-details.json) supplies the
arrondissement / height / circumference data needed by the geographic
and demesure categories.
*/
export const kBadgeCatalog: BadgeDef[] = [
	{ id: 'first_capture',         label: 'Première capture',     description: 'Tu as croisé ton premier arbre.',           category: 'decouverte',   implemented: true },
	{ id: 'promenade',             label: 'Promenade',            description: '10 captures.',                              category: 'decouverte',   implemented: true },
	{ id: 'marcheur',              label: 'Marcheur',             description: '50 captures.',                              category: 'decouverte',   implemented: true },
	{ id: 'centurion',             label: 'Centurion',            description: '100 captures.',                             category: 'decouverte',   implemented: true },

	{ id: 'botaniste_amateur',     label: 'Botaniste amateur',    description: '50 espèces différentes.',                   category: 'botanique',    implemented: true },
	{ id: 'botaniste_confirme',    label: 'Botaniste confirmé',   description: '200 espèces différentes.',                  category: 'botanique',    implemented: true },
	{ id: 'espece_rare',           label: 'Espèce rare',          description: 'Une espèce avec moins de 100 individus à Paris.', category: 'botanique', implemented: true },

	{ id: 'tourneur_de_paris',     label: 'Tourneur de Paris',    description: 'Captures dans 10 arrondissements.',         category: 'geographie',   implemented: true },
	{ id: 'tour_complet',          label: 'Tour complet',         description: 'Les 20 arrondissements de Paris.',          category: 'geographie',   implemented: true },

	{ id: 'chasseur_remarquables', label: 'Chasseur de remarquables', description: '10 arbres remarquables.',               category: 'remarquables', implemented: true },
	{ id: 'legende',               label: 'Légende',              description: '50 arbres remarquables.',                   category: 'remarquables', implemented: true },

	{ id: 'ronde_des_saisons',     label: 'Ronde des saisons',    description: 'Au moins une capture dans les 4 saisons.',  category: 'saisons',      implemented: true },
	{ id: 'annee_complete',        label: 'Année complète',       description: 'Une capture chaque mois sur 12 mois consécutifs.', category: 'saisons', implemented: true },

	{ id: 'geant',                 label: 'Géant',                description: 'Un arbre de plus de 30 m de haut.',         category: 'demesure',     implemented: true },
	{ id: 'vieux_sage',            label: 'Vieux sage',           description: 'Un arbre de plus de 4 m de circonférence.', category: 'demesure',     implemented: true },
]

/*
Glyph assigned to each badge category. The badges screen uses these on
both unlocked and locked cards (locked cards desaturate via CSS rather
than swap the glyph).
*/
export const kBadgeGlyphs: Record<BadgeCategory, string> = {
	decouverte: '🌱',
	botanique: '🌿',
	geographie: '🗺️',
	remarquables: '⭐',
	saisons: '🍂',
	demesure: '🌳',
}
